import { Box, Button, Paper, Stack, Typography } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { PATHS } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { useNavigate } from 'react-router-dom'
import { useYardIssueCapacityContext } from '../../hooks/useYardIssueCapacityContext'
import { YardIssueDto, YardIssueTypeDummy } from '../../models/yard-issue.model'
import { IssueCardBody } from './IssueCardBody'

interface Props {
  issue: YardIssueDto
  hasResolve?: boolean
  onClickResolve?: () => void
}

const CAPACITY_OVERFLOW_TYPES: YardIssueTypeDummy[] = [
  YardIssueTypeDummy.AllocationRuleCapacityFull,
  YardIssueTypeDummy.BlockCapacityFull,
  YardIssueTypeDummy.YardCapacityFull,
]

export const YardIssueCard = observer(({ issue, hasResolve, onClickResolve }: Props) => {
  const { t } = useTranslate()
  const navigate = useNavigate()
  const isCapacityOverflow = CAPACITY_OVERFLOW_TYPES.includes(issue.issueType)
  const capacityContext = useYardIssueCapacityContext(issue)

  const issueTypeTranslationMap: Record<YardIssueTypeDummy, string> = {
    [YardIssueTypeDummy.AllocationOverflow]: t('allocationOverflow', 'Allocation overflow'),
    [YardIssueTypeDummy.DuplicatePosition]: t('duplicatePosition', 'Duplicate position'),
    [YardIssueTypeDummy.FlyingContainer]: t('flyingContainer', 'Flying container'),
    [YardIssueTypeDummy.ContainerInBlockedSlot]: t(
      'containerInBlockedSlot',
      'Container in blocked slot',
    ),
    [YardIssueTypeDummy.ContainerOutOfBounds]: t('containerOutOfBounds', 'Container out of bounds'),
    [YardIssueTypeDummy.StackHeightExceeded]: t('stackHeightExceeded', 'Stack height exceeded'),
    [YardIssueTypeDummy.ReeferContainerInUnpoweredSlot]: t(
      'reeferContainerInUnpoweredSlot',
      'Reefer container in unpowered slot',
    ),
    [YardIssueTypeDummy.NonReeferContainerInReeferSlot]: t(
      'nonReeferContainerInReeferSlot',
      'Non-reefer container in reefer slot',
    ),
    [YardIssueTypeDummy.AllocationRuleCapacityFull]: t(
      'allocationRuleCapacityFull',
      'Allocation rule full',
    ),
    [YardIssueTypeDummy.BlockCapacityFull]: t('blockCapacityFull', 'Block full'),
    [YardIssueTypeDummy.YardCapacityFull]: t('yardCapacityFull', 'Yard full'),
    [YardIssueTypeDummy.ImdgSegregationViolation]: t(
      'imdgSegregationViolation',
      'IMDG segregation violation',
    ),
    [YardIssueTypeDummy.ImdgStackingOnTopViolation]: t(
      'imdgStackingOnTopViolation',
      'IMDG stacking violation',
    ),
    [YardIssueTypeDummy.ImdgTierCapViolation]: t('imdgTierCapViolation', 'IMDG tier cap violation'),
    [YardIssueTypeDummy.Unknown]: t('unknown', 'Unknown'),
  }

  return (
    <Paper variant='outlined'>
      <Box sx={{ padding: '1rem', display: 'flex' }}>
        <Stack
          direction='row'
          spacing={1}
          alignItems='center'
          justifyContent='space-between'
          width='100%'
        >
          <Stack>
            <Typography variant='h4'>{issueTypeTranslationMap[issue.issueType]}</Typography>
            <IssueCardBody issue={issue} capacityContext={capacityContext} />
          </Stack>
          <Stack direction='row' spacing={1} alignItems='center'>
            {hasResolve && isCapacityOverflow && (
              <Button
                size='medium'
                variant='contained'
                onClick={() => navigate(PATHS.yard.view.url)}
              >
                {t('openYard', 'Open yard')}
              </Button>
            )}
            {hasResolve && !isCapacityOverflow && (
              <Button size='medium' variant='contained' onClick={onClickResolve}>
                {t('resolve', 'Resolve')}
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>
    </Paper>
  )
})
